import React from "react";
import ReactDOM from "react-dom";
import "./App.css";
import { ZoomMtg } from "@zoom/meetingsdk";

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();

const socketUrl = process.env.REACT_APP_SOCKET_URL || "wss://https://stgapi.nrichlearning.co.in/ws";

class App extends React.Component {
  pauseAndPlayRecSubscription;
  iconSubscription;
  classId;
  instituteId;
  accessToken;
  role;
  isJoinClass;
  meetingDetails = null;
  feedbackEnabled = false;
  feedbackCollectionEnabled = false;
  ws = null;
  wsReconnectAttempts = 0;
  wsMaxReconnects = 5;
  wsReconnectTimer = null;
  feedbackDismissed = false;
  sessionEnded = false;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      checkingDesktop: false,
      showWebFallback: false,
      showAppLaunchPrompt: false,
      showFeedbackForm: false,
      feedbackText: "",
      selectedRating: null,
      feedbackSubmitted: false,
    };
  }

  createAndSubscribeSocket(userId) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.ws = new WebSocket(socketUrl);
    this._wsMsgCount = 0;

    this.ws.onopen = () => {
      this.wsReconnectAttempts = 0;
      const connectFrame =
        "CONNECT\n" +
        `Authorization:Bearer ${this.accessToken}\n` +
        "accept-version:1.1,1.2\n" +
        "heart-beat:0,0\n" +
        "\n\0";
      this.ws.send(connectFrame);
    };

    this.ws.onmessage = (event) => {
      if (typeof event.data === "string" && event.data.startsWith("CONNECTED")) {
        const subscribeFrame =
          "SUBSCRIBE\n" +
          "id:sub-0\n" +
          "destination:/user/queue/feedback\n" +
          "\n\0";
        this.ws.send(subscribeFrame);
        return;
      }

      if (this.role?.toLowerCase() === "student" ) {
        this.feedbackEnabled = true;
        this.feedbackDismissed = false;
        if (this.isJoinClass) {
          this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
        }
      }
    };

    this.ws.onerror = (error) => {
      console.error("[WS] Error:", error);
    };

    this.ws.onclose = () => {
      this.attemptReconnect(userId);
    };
  }

  attemptReconnect(userId) {
    if (this.wsReconnectAttempts >= this.wsMaxReconnects) {
      return;
    }
    const delay = Math.min(1000 * 2 ** this.wsReconnectAttempts, 30000);
    this.wsReconnectAttempts += 1;
    this.wsReconnectTimer = setTimeout(() => {
      this.createAndSubscribeSocket(userId);
    }, delay);
  }

  async onParticipantExit() {
    try {
      await fetch(
        process.env.REACT_APP_API_URL + "users/v1/api/participants/exit/" + this.classId,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
        }
      );
      this.clearSessionId();
    } catch (error) {
      console.error("[Exit] Error:", error);
    }
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("unload", this.handleUnload);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    try {
      const query = window.location.search.split("?")[1] || "";
      let params = query.split("&", 5);
      this.classId = params[0];
      this.instituteId = params[1];
      this.accessToken = params[2];
      this.role = params[3];
      const isFromLMS = params[4];
      const isFromSession = sessionStorage.getItem("isFromLMS") === "true";

      if (isFromLMS) {
        sessionStorage.setItem("isFromLMS", "true");
        const cleanParams = params.slice(0, 4);
        const newUrl = `${window.location.pathname}?${cleanParams.join("&")}`;
        window.history.replaceState({}, "", newUrl);
      }

      if (this.classId && this.accessToken && (isFromLMS || isFromSession)) {
        this.initializeMeeting();
        this.onParticipantJoining();
      } else {
        window.location.href = "/invalid-link";
      }
    } catch (err) {
      window.location.href = "/invalid-link";
    }
  }

  handleVisibilityChange = () => {
    if (this.sessionEnded) return;
    if (document.visibilityState === "visible") {
      this.onParticipantJoining();
    } else {
      this.onParticipantExit();
      this.clearSessionId();
    }
  };

  handleUnload = () => {
    this.onParticipantExit();
    this.clearSessionId();
  };

  handleBeforeUnload = () => {
    this.onParticipantExit();
    this.clearSessionId();
  };

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("unload", this.handleUnload);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.onParticipantExit();
    if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }
  }

  clearSessionId() {
    localStorage.removeItem("session_id");
  }

  initializeMeeting() {
    this.createAndSubscribeSocket(this.parseJwt(this.accessToken));

    fetch(
      process.env.REACT_APP_API_URL + "users/v1/api/getMeetingDetails?idClassSchedule=" + this.classId,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        if (response.status === 201) {
          this.meetingDetails = {
            meetingNumber: response.body.id,
            userName: response.body.name,
            userEmail: response.body.hostEmail,
            passWord: response.body.password,
            role: response.body.role,
            userId: response.body.userId,
            signature: response.body.signature,
            responseBody: response.body,
          };

          if (response.body.endTime <= 0) {
            this.closeTab("This class has already ended. You cannot join.");
            return;
          }

          this.fetchClassSettings(response.body.endTime);

          if (this.role === "Student") {
            this.setState({ checkingDesktop: false });
            this.handleJoinWeb();
          } else  {
            this.setState({ isLoading: false, showAppLaunchPrompt: true });
          } 
        } else if (response.status === 401) {
          this.closeTab("You are already logout.");
        } else {
          this.closeTab("Something went wrong");
        }
      })
      .catch((error) => {
        console.error("Error while initializing meeting:", error);
      });
  }

  getDeviceType() {
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) return "android";
    if (/iPhone|iPad/i.test(ua)) return "ios";
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
    return "desktop";
  }

  buildZoomAppUrl() {
    const { meetingNumber, passWord } = this.meetingDetails;
    const encodedPassword = encodeURIComponent(passWord || "");
    const device = this.getDeviceType();
    if (device === "android") {
      return `intent://zoom.us/join?confno=${meetingNumber}&pwd=${encodedPassword}#Intent;scheme=zoomus;package=us.zoom.videomeetings;end`;
    }
    const scheme = device === "ios" ? "zoomus" : "zoommtg";
    return `${scheme}://zoom.us/join?confno=${meetingNumber}&pwd=${encodedPassword}`;
  }

  attemptAppJoin = () => {
    this.setState({ checkingDesktop: true, showWebFallback: false, showAppLaunchPrompt: false });

    const zoomURL = this.buildZoomAppUrl();
    let checkTimer = null;
    let fallbackRevealTimer = null;
    let visibilityTimer = null;
    let didClose = false;

    const handleVisibilityChange = () => {
      if (document.hidden && !didClose) {
        visibilityTimer = setTimeout(() => {
          if (document.hidden && !didClose) {
            didClose = true;
            cleanup();
            this.onParticipantJoining();
            window.close();
          }
        }, 2000);
      } else if (!document.hidden && visibilityTimer) {
        clearTimeout(visibilityTimer);
        visibilityTimer = null;
      }
    };

    const cleanup = () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (checkTimer) clearTimeout(checkTimer);
      if (fallbackRevealTimer) clearTimeout(fallbackRevealTimer);
      if (visibilityTimer) clearTimeout(visibilityTimer);
      this.webFallbackHandler = null;
    };

    this.webFallbackHandler = () => {
      if (didClose) return;
      didClose = true;
      cleanup();
      this.setState({ checkingDesktop: false, showWebFallback: false });
      this.handleJoinWeb();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.location.href = zoomURL;

    fallbackRevealTimer = setTimeout(() => {
      if (!didClose && !document.hidden) {
        this.setState({ showWebFallback: true });
      }
    }, 3000);

    checkTimer = setTimeout(() => {
      if (didClose) return;
      const isHidden = document.hidden;
      const hasFocus = document.hasFocus();
      if (isHidden || !hasFocus) {
        didClose = true;
        cleanup();
        this.onParticipantJoining();
        window.close();
      } else {
        didClose = true;
        cleanup();
        this.setState({ checkingDesktop: false, showWebFallback: false });
        this.handleJoinWeb();
      }
    }, 5000);
  };

  handleJoinWeb = () => {
    this.setState({ showPopup: false, isLoading: true });
    const root = document.getElementById("zmmtg-root");
    if (root) root.style.display = "block";
    const { meetingNumber, userName, userEmail, passWord, userId, signature } = this.meetingDetails;
    this.joinViaWebSDK(meetingNumber, userName, userEmail, passWord, userId, signature);
  };

  joinViaWebSDK(meetingNumber, userName, userEmail, passWord, userId, signature) {
    ZoomMtg.init({
      leaveUrl: process.env.REACT_APP_LEAVE_URL,
      disableInvite: true,
      disableZoomLogo: true,
      defaultView: "gallery",
      patchJsMedia: true,
      leaveOnPageUnload: true,
      success: () => {
        ZoomMtg.join({
          signature: signature,
          meetingNumber: meetingNumber,
          userName: userName == null ? "Test" : userName,
          sdkKey: process.env.REACT_APP_SDK_KEY,
          customerKey: userId + "/" + this.classId,
          userEmail: userEmail,
          passWord: passWord,
          success: () => {
            this.isJoinClass = true;
            this.setState({ isLoading: false });
            this.tryShowFeedbackForm();
            this.onParticipantJoining();
            const appSignalElement = document.getElementById("app-signal");
            if (appSignalElement) appSignalElement.style.display = "none";
            this.removePauseAndPlayRecordingBtn();
            this.removeRecordingIcons();
          },
          error: (error) => {
            console.log("Zoom join error:", error);
            this.setState({ isLoading: false });
            alert("Failed to join meeting. Please try again.");
          },
        });
      },
      error: (error) => {
        console.log("Zoom init error:", error);
        this.setState({ isLoading: false });
        alert("Failed to initialize Zoom. Please try again.");
      },
    });
  }

  closeTab(msg) {
    alert(msg);
    setTimeout(() => {
      window.close();
    }, 2000);
  }

  async onParticipantJoining() {
    if (this.sessionEnded) return;
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "users/v1/api/participants/entry/" + this.classId,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
        }
      );
      const responseData = await response.json();
      if (responseData.status === 400) {
        this.closeTab(responseData.message);
      }
    } catch (err) {
      console.error("Error during participant join:", err);
    }
  }

  removePauseAndPlayRecordingBtn() {
    this.pauseAndPlayRecSubscription = setInterval(() => {
      var htmlCollection = document.getElementsByClassName("footer-button-pause-stop-recording");
      if (htmlCollection && htmlCollection.length !== 0) {
        for (let element of htmlCollection) element.remove();
        clearInterval(this.pauseAndPlayRecSubscription);
      }
    }, 500);
  }

  removeRecordingIcons() {
    this.iconSubscription = setInterval(() => {
      var htmlCollection = document.getElementsByClassName("meeting-info-container__wrapper");
      if (htmlCollection && htmlCollection.length !== 0) {
        for (let element of htmlCollection) element.remove();
        clearInterval(this.iconSubscription);
      }
    }, 500);
  }

  parseJwt(token) {
    try {
      const base64Payload = token.split(".")[1];
      const decoded = atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/"));
      const payload = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(payload);
    } catch (e) {
      console.error("Invalid JWT token parsing:", e);
      return {};
    }
  }

  tryShowFeedbackForm() {
    if (
      this.isJoinClass &&
      this.feedbackEnabled &&
      this.role === "Student" &&
      !this.feedbackDismissed
    ) {
      this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
    }
  }

  fetchClassSettings(endTime) {
    fetch(
      process.env.REACT_APP_API_URL + "users/v2/api/settings/class/" + this.instituteId,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.accessToken,
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        const enabled = response?.body?.feedbackCollectionEnabled;
        if (enabled === true || enabled === "true" || enabled === 1) {
          this.feedbackCollectionEnabled = true;
        }
      })
      .catch((error) => {
        console.error("Error fetching class settings:", error);
      })
      .finally(() => {
        const fiveMinMs = 5 * 60 * 1000;
        const feedbackDelay = Math.max(0, endTime * 1000 - fiveMinMs);

        setTimeout(() => {
          if (
            (this.role === "Student" || this.role === "STUDENT") &&
            this.isJoinClass &&
            !this.feedbackDismissed
          ) {
            this.setState({ showFeedbackForm: true, feedbackSubmitted: false });
          }
        }, feedbackDelay);

        this.sessionOut(endTime);
      });
  }

  sessionOut(endTime) {
    setTimeout(() => {
      this.sessionEnded = true;
      if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close();
      }
      alert("Session Timeout.");
      setTimeout(() => {
        window.close();
      }, 5000);
    }, endTime * 1000);
  }

  async submitFeedback() {
    const { feedbackText, selectedRating } = this.state;
    if (!selectedRating || !feedbackText.trim()) return;
    try {
      await fetch(
        process.env.REACT_APP_API_URL + "users/v2/api/feedback/submission",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.accessToken,
          },
          body: JSON.stringify({
            id: null,
            classId: this.classId,
            review: feedbackText.trim(),
            reaction: selectedRating,
             studentId: null,
          }),
        }
      );
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
    this.feedbackDismissed = true;
    this.setState({
      showFeedbackForm: false,
      feedbackSubmitted: true,
      feedbackText: "",
      selectedRating: null,
    });
  }

  render() {
    const {
      isLoading,
      checkingDesktop,
      showWebFallback,
      showAppLaunchPrompt,
      showFeedbackForm,
      feedbackText,
      selectedRating,
    } = this.state;

    const ratings = [
      { emoji: "😍", label: "Amazing",      value: "Amazing" },
      { emoji: "😊", label: "Good",          value: "Good" },
      { emoji: "😐", label: "Average",       value: "Average" },
      { emoji: "😕", label: "Below Average", value: "Below Average" },
    ];

    const feedbackPortal = showFeedbackForm
      ? ReactDOM.createPortal(
          <div style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 2147483647,
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e8",
            borderRadius: "10px",
            padding: "24px",
            width: "320px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
            fontFamily: "'Poppins', sans-serif",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
              <div>
                <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px" }}>
                  How was your experience?
                </h2>
                <p style={{ fontSize: "0.82rem", color: "#999", margin: 0 }}>
                  We'd love to hear your feedback
                </p>
              </div>
              <button
                onClick={() => {
                  this.feedbackDismissed = true;
                  this.setState({ showFeedbackForm: false, feedbackText: "", selectedRating: null });
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "#999",
                  lineHeight: 1,
                  padding: "0",
                  marginLeft: "8px",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-around", marginBottom: "24px" }}>
              {ratings.map((r) => (
                <button
                  key={r.value}
                  onClick={() => this.setState({ selectedRating: r.value })}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                    background: selectedRating === r.value ? "#fff8ec" : "none",
                    border: `2px solid ${selectedRating === r.value ? "#f5a623" : "transparent"}`,
                    borderRadius: "14px",
                    padding: "10px 12px",
                    cursor: "pointer",
                    transition: "all 0.22s",
                  }}
                >
                  <span style={{
                    fontSize: "2rem",
                    lineHeight: 1,
                    display: "block",
                    transform: selectedRating === r.value ? "scale(1.25)" : "scale(1)",
                    transition: "transform 0.22s",
                  }}>
                    {r.emoji}
                  </span>
                  <span style={{
                    fontSize: "9px",
                    color: selectedRating === r.value ? "#c47d00" : "#999",
                    fontWeight: selectedRating === r.value ? 700 : 500,
                    whiteSpace: "nowrap",
                    fontFamily: "'Poppins', sans-serif",
                  }}>
                    {r.label}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontSize: "0.82rem",
                fontWeight: 700,
                color: "#1a1a1a",
                marginBottom: "8px",
                fontFamily: "'Poppins', sans-serif",
              }}>
                Your Review
              </label>
              <textarea
                rows={4}
                value={feedbackText}
                onChange={(e) => this.setState({ feedbackText: e.target.value })}
                placeholder="Tell us more about your experience..."
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: "#f7f8fa",
                  border: "1.5px solid #e8e8e8",
                  borderRadius: "10px",
                  padding: "12px 14px",
                  fontSize: "0.85rem",
                  fontFamily: "'Poppins', sans-serif",
                  color: "#1a1a1a",
                  resize: "vertical",
                  outline: "none",
                }}
              />
            </div>

            <button
              onClick={() => this.submitFeedback()}
              disabled={!selectedRating || !feedbackText.trim()}
              style={{
                width: "100%",
                padding: "13px",
                border: "none",
                borderRadius: "10px",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "0.9rem",
                fontWeight: 600,
                letterSpacing: "0.3px",
                cursor: (selectedRating && feedbackText.trim()) ? "pointer" : "default",
                background: (selectedRating && feedbackText.trim()) ? "#4a4a4a" : "#c5c8cc",
                color: "#fff",
                boxShadow: (selectedRating && feedbackText.trim()) ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
                transition: "background 0.25s, box-shadow 0.25s",
              }}
            >
              Submit Feedback
            </button>
          </div>,
          document.body
        )
      : null;

    return (
      <div className="App" style={{ textAlign: "center" }}>
        {showAppLaunchPrompt && (
          <div style={{
            marginTop: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "15px",
            padding: "0 20px",
          }}>
            <p style={{ color: "#111827", fontSize: "17px", fontWeight: 600 }}>
              Join your class
            </p>
            <button
              onClick={() => this.attemptAppJoin()}
              style={{
                padding: "12px 24px",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
                maxWidth: "320px",
              }}
            >
              Join with Zoom App
            </button>
            <button
              onClick={() => {
                this.setState({ showAppLaunchPrompt: false });
                this.handleJoinWeb();
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "transparent",
                color: "#2563eb",
                border: "1px solid #2563eb",
                borderRadius: "6px",
                fontSize: "15px",
                cursor: "pointer",
                width: "100%",
                maxWidth: "320px",
              }}
            >
              Join in Browser
            </button>
          </div>
        )}
        {(isLoading || checkingDesktop) && !showAppLaunchPrompt && (
          <div style={{
            marginTop: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "15px",
          }}>
            <div style={{
              width: "50px",
              height: "50px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}></div>
            <p style={{ color: "#6b7280", fontSize: "16px" }}>
              Loading...
            </p>
            {showWebFallback && (
              <button
                onClick={() => {
                  if (this.webFallbackHandler) this.webFallbackHandler();
                }}
                style={{
                  marginTop: "10px",
                  padding: "10px 20px",
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                Join in Browser
              </button>
            )}
          </div>
        )}
        {feedbackPortal}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
}

export default App;